import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  SettingsDrawer,
  accountSection,
  securitySection,
  type AdminSection,
  type AuditSession,
  type ViewerTier,
} from '@cca/ui';
import {
  api,
  ApiError,
  type EligibleEditor,
  type MeResponse,
  type ParishSummary,
} from '../api-client';
import { signOutThisDevice, signOutAllDevices } from './signout';

const ACCENT = '#85754d'; // Parish Portal gold

/**
 * Parish Portal Settings — the estate-standard role-aware drawer (replaces the
 * bespoke AdminDrawer). Account for everyone; Security (login audit) and the
 * "Request a parish update" tool for the registrar. See the portal-admin standard.
 */
export function ParishSettings({
  open,
  onClose,
  viewer,
  me,
}: {
  open: boolean;
  onClose: () => void;
  viewer: ViewerTier;
  me: MeResponse;
}): JSX.Element {
  const [sessions, setSessions] = useState<AuditSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | undefined>(undefined);

  // Only the registrar has a login-history endpoint; fetch lazily on open.
  useEffect(() => {
    if (!open || viewer !== 'superuser') return;
    let alive = true;
    api
      .loginHistory()
      .then(({ sessions }) => alive && setSessions(sessions))
      .catch(
        (e) =>
          alive && setSessionsError(e instanceof ApiError ? e.message : 'Could not load sign-ins.'),
      );
    return () => {
      alive = false;
    };
  }, [open, viewer]);

  const sections: AdminSection[] = [
    accountSection({
      identity: (
        <>
          Signed in as <strong style={{ color: 'var(--color-ink, #111827)' }}>{me.email}</strong>.
        </>
      ),
      onSignOutThisDevice: signOutThisDevice,
      onSignOutAll: signOutAllDevices,
    }),
    securitySection({
      title: 'Security',
      minRole: 'superuser',
      sessions,
      accent: ACCENT,
      ...(sessionsError ? { sessionsError } : {}),
    }),
    {
      key: 'operations',
      title: 'Request a parish update',
      minRole: 'superuser',
      body: <RequestUpdateSection parishes={me.parishes} />,
    },
  ];

  return (
    <SettingsDrawer
      open={open}
      onClose={onClose}
      viewer={viewer}
      sections={sections}
      accent={ACCENT}
    />
  );
}

type SendResult = { email: string; result: 'sent' | 'duplicate' | 'failed' | 'not_eligible' };

/**
 * Registrar tool: pick a parish, choose from its eligible editors, and email
 * them a link asking them to review/update the parish's details. Recipients
 * sign in with their own OTP — no token is embedded in the link.
 */
function RequestUpdateSection({ parishes }: { parishes: ParishSummary[] }): JSX.Element {
  const sortedParishes = useMemo(
    () => [...parishes].sort((a, b) => a.name.localeCompare(b.name)),
    [parishes],
  );
  const [parishId, setParishId] = useState('');
  const [editors, setEditors] = useState<EligibleEditor[] | null>(null);
  const [loadingEditors, setLoadingEditors] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<SendResult[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setEditors(null);
    setSelected(new Set());
    setResults(null);
    setErr(null);
    if (!parishId) return;
    let alive = true;
    setLoadingEditors(true);
    api
      .eligibleEditors(parishId)
      .then((r) => alive && setEditors(r.editors))
      .catch((e) => alive && setErr(e instanceof ApiError ? e.message : 'Could not load editors.'))
      .finally(() => alive && setLoadingEditors(false));
    return () => {
      alive = false;
    };
  }, [parishId]);

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const send = async () => {
    if (!parishId || selected.size === 0 || !editors) return;
    setSending(true);
    setErr(null);
    setResults(null);
    try {
      const recipients = editors
        .filter((e) => selected.has(e.email))
        .map((e) => ({ email: e.email, name: e.name }));
      const res = await api.notifyUpdate(parishId, recipients, message.trim() || undefined);
      setResults(res.sent);
      setSelected(new Set());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  const resultLabel: Record<SendResult['result'], string> = {
    sent: 'Sent',
    duplicate: 'Already sent in the last 24h',
    failed: 'Failed to send',
    not_eligible: 'No longer an eligible editor',
  };

  return (
    <div>
      <p className="mb-3 text-[12px] leading-relaxed text-ccaanz-grey">
        Email a parish's editors a link asking them to review and update their details. They sign in
        with their own login. Each person can be re-notified once every 24 hours.
      </p>

      <label htmlFor="ru-parish" className="mb-1 block text-[12px] text-ccaanz-grey">
        Parish
      </label>
      <select
        id="ru-parish"
        className="input mb-3 w-full"
        value={parishId}
        onChange={(e) => setParishId(e.target.value)}
      >
        <option value="">Select a parish…</option>
        {sortedParishes.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>

      {parishId && loadingEditors && (
        <p className="text-[12px] text-ccaanz-grey">Loading editors…</p>
      )}

      {parishId && !loadingEditors && editors !== null && editors.length === 0 && (
        <p className="text-[12px] text-ccaanz-amber">
          No eligible editors with an email on record for this parish. Add a Senior Minister,
          Warden, Treasurer or Administrator (with an email) in the CRM first.
        </p>
      )}

      {editors !== null && editors.length > 0 && (
        <>
          <p className="mb-2 text-[12px] text-ccaanz-grey">Who should be notified?</p>
          <ul className="mb-3 space-y-1.5">
            {editors.map((ed) => (
              <li key={ed.contact_id}>
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.has(ed.email)}
                    onChange={() => toggle(ed.email)}
                  />
                  <span className="text-[12px] leading-snug">
                    <span className="font-medium text-ccaanz-black">{ed.name}</span>{' '}
                    <span className="text-ccaanz-grey">· {ed.email}</span>
                    <br />
                    <span className="text-[11px] text-ccaanz-grey">{ed.roles.join(', ')}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <label htmlFor="ru-message" className="mb-1 block text-[12px] text-ccaanz-grey">
            Note (optional)
          </label>
          <textarea
            id="ru-message"
            className="input mb-3 w-full"
            rows={2}
            placeholder="e.g. We need your 2026 key-personnel return by month end."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <button
            type="button"
            className="btn btn-primary"
            disabled={sending || selected.size === 0}
            onClick={send}
          >
            {sending
              ? 'Sending…'
              : `Send to ${selected.size || ''} ${selected.size === 1 ? 'person' : 'people'}`.trim()}
          </button>
        </>
      )}

      {err && <p className="mt-3 text-[12px] text-ccaanz-red">{err}</p>}

      {results && (
        <ul className="mt-3 space-y-1 text-[12px]">
          {results.map((r) => (
            <li
              key={r.email}
              className={r.result === 'sent' ? 'text-ccaanz-green' : 'text-ccaanz-grey'}
            >
              {r.result === 'sent' ? '✓' : '•'} {r.email} — {resultLabel[r.result]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
